import { CommonModule } from '@angular/common';
import { Component, HostListener, inject, ElementRef, ViewChildren, QueryList, AfterViewInit, OnDestroy, ChangeDetectorRef, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CropSelectionRequest, CropType } from '../../data/models/job-submit.models';
import { JobProcessingStateService } from '../../data/services/job-processing-state.service';
import { JobsService } from '../../data/services/jobs.service';
import { CompanyContextService } from '../../../../core/services/company-context.service';

interface CropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface CropOption {
  id: string;
  title: string;
  imagePath: string;
  referenceImageUrl: string;
  selected: boolean;

  // Saved image-space crop origin from the API (null = no saved position)
  savedCropX: number | null;
  savedCropY: number | null;

  naturalWidth: number;
  naturalHeight: number;

  fitScale: number;
  userScale: number;
  minUserScale: number;
  maxUserScale: number;

  translateX: number;
  translateY: number;
}

interface DragState {
  optionId: string;
  startClientX: number;
  startClientY: number;
  initialTranslateX: number;
  initialTranslateY: number;
}

@Component({
  selector: 'app-job-crops',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './job-crops.html',
  styleUrl: './job-crops.scss'
})
export class JobCrops implements OnInit, AfterViewInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly jobsService = inject(JobsService);
  private readonly jobProcessingStateService = inject(JobProcessingStateService);
  private readonly companyContextService = inject(CompanyContextService);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly jobId = Number(this.route.snapshot.paramMap.get('id'));

  isLoading = false;
  isSavingCrops = false;
  errorMessage = '';
  showNoCropAlert = false;

  visibleCropCount = 4;
  private cropsSetFromQueryParam = false;

  @ViewChildren('previewContainer') previewContainers!: QueryList<ElementRef>;
  private resizeObserver: ResizeObserver | null = null;

  constructor() {
    const qp = this.route.snapshot.queryParamMap.get('cropsCount');
    const parsed = qp !== null ? parseInt(qp, 10) : NaN;
    if (!isNaN(parsed) && parsed > 0) {
      this.visibleCropCount = Math.min(4, Math.max(1, parsed));
      this.cropsSetFromQueryParam = true;
    }
  }

  previewWidth = 220;
  previewHeight = 285;

  fixedCropRect: CropRect = {
    x: 30,
    y: 42,
    width: 160,
    height: 200
  };

  cropOptions: CropOption[] = [];

  private dragState: DragState | null = null;

  ngOnInit(): void {
    this.loadPageData();
  }

  ngAfterViewInit(): void {
    this.setupResizeObserver();
  }

  ngOnDestroy(): void {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  setupResizeObserver(): void {
    this.resizeObserver = new ResizeObserver((entries) => {
      if (entries.length > 0) {
        let maxRectWidth = 0;
        for (const entry of entries) {
          if (entry.contentRect.width > maxRectWidth) {
            maxRectWidth = entry.contentRect.width;
          }
        }

        if (maxRectWidth > 0 && Math.abs(this.previewWidth - maxRectWidth) > 2) {
          this.updateDimensions(maxRectWidth);
        }
      }
    });

    const observeContainers = () => {
      this.resizeObserver?.disconnect();
      if (this.previewContainers) {
        this.previewContainers.forEach(container => {
          this.resizeObserver?.observe(container.nativeElement);
        });
      }
    };

    observeContainers();
    this.previewContainers.changes.subscribe(() => {
      observeContainers();
    });
  }

  updateDimensions(newWidth: number): void {
    this.previewWidth = newWidth;

    const paddingX = Math.round(newWidth * 0.1);
    this.fixedCropRect.x = paddingX;
    this.fixedCropRect.width = Math.round(newWidth - 2 * paddingX);

    this.fixedCropRect.height = Math.round(this.fixedCropRect.width * 1.25);

    const paddingY = paddingX;
    this.fixedCropRect.y = paddingY;

    this.previewHeight = this.fixedCropRect.height + 2 * paddingY;

    this.cropOptions.forEach(option => {
      if (option.naturalWidth > 0 && option.naturalHeight > 0) {
        option.fitScale = Math.min(
          this.previewWidth / option.naturalWidth,
          this.previewHeight / option.naturalHeight
        );

        const coverScale = Math.max(
          this.fixedCropRect.width / (option.naturalWidth * option.fitScale),
          this.fixedCropRect.height / (option.naturalHeight * option.fitScale)
        );

        option.minUserScale = Math.max(0.35, +(coverScale * 0.55).toFixed(2));

        option.userScale = Math.max(option.userScale, option.minUserScale);

        const clamped = this.clampImagePosition(option, option.translateX, option.translateY);
        option.translateX = clamped.x;
        option.translateY = clamped.y;
      }
    });

    this.cropOptions = [...this.cropOptions];
    this.cdr.detectChanges();
  }

  get selectedCount(): number {
    return this.visibleCropOptions.filter(option => option.selected).length;
  }

  get visibleCropOptions(): CropOption[] {
    return this.cropOptions.slice(0, this.visibleCropCount);
  }

  loadPageData(): void {
    this.isLoading = true;
    this.errorMessage = '';

    const companyId = this.companyContextService.selectedCompanyId();

    forkJoin({
      job: this.jobsService.getJobById(this.jobId),
      cropSelectionsResponse: this.jobsService.getCropSelections(this.jobId, companyId).pipe(
        catchError(() => of(null))
      )
    }).subscribe({
      next: ({ job, cropSelectionsResponse }) => {
        this.jobProcessingStateService.setJobFlags({
          jobId: this.jobId,
          isEnhancements: job.enhanceImages,
          isCropping: job.cropImages,
          isBackgroundExtraction: job.extractBackground,
          isBestPick: job.pickBestImage
        });

        if (!this.cropsSetFromQueryParam) {
          const stateCount = this.jobProcessingStateService.getState(this.jobId).cropsPerImagePose;
          if (stateCount > 1) {
            this.visibleCropCount = stateCount;
          } else {
            const raw = job.cropsPerImagePose;
            if (raw !== null && raw !== undefined && raw > 0) {
              this.visibleCropCount = Math.min(4, Math.max(1, raw));
            }
          }
        }

        const selections = cropSelectionsResponse?.data?.selections ?? [];

        if (selections.length > 0) {
          this.cropOptions = selections.map(sel => {
            // A non-zero width means a real crop position was saved by the user
            const hasSavedPosition = (sel.width ?? 0) > 0;
            return {
              id: sel.cropType,
              title: sel.cropType,
              imagePath: sel.referenceImageUrl ?? '/images/sample_enhancement_1.jpg',
              referenceImageUrl: sel.referenceImageUrl ?? '',
              selected: false,
              savedCropX: hasSavedPosition ? (sel.x ?? null) : null,
              savedCropY: hasSavedPosition ? (sel.y ?? null) : null,
              naturalWidth: 0,
              naturalHeight: 0,
              fitScale: 1,
              userScale: sel.scale !== null && sel.scale !== undefined ? sel.scale : 1,
              minUserScale: 1,
              maxUserScale: 5,
              translateX: 0,
              translateY: 0
            } as CropOption;
          });
        } else {
          this.cropOptions = [
            { id: 'YEAR_BOOK', title: 'Crop 1', imagePath: '/images/sample_enhancement_1.jpg', referenceImageUrl: '', selected: false, savedCropX: null, savedCropY: null, naturalWidth: 0, naturalHeight: 0, fitScale: 1, userScale: 1, minUserScale: 1, maxUserScale: 5, translateX: 0, translateY: 0 },
            { id: 'HEAD_SHOULDER', title: 'Crop 2', imagePath: '/images/sample_enhancement_1.jpg', referenceImageUrl: '', selected: false, savedCropX: null, savedCropY: null, naturalWidth: 0, naturalHeight: 0, fitScale: 1, userScale: 1, minUserScale: 1, maxUserScale: 5, translateX: 0, translateY: 0 },
            { id: 'THREE_QUARTER', title: 'Crop 3', imagePath: '/images/sample_enhancement_1.jpg', referenceImageUrl: '', selected: false, savedCropX: null, savedCropY: null, naturalWidth: 0, naturalHeight: 0, fitScale: 1, userScale: 1, minUserScale: 1, maxUserScale: 5, translateX: 0, translateY: 0 },
            { id: 'FULL_LENGTH', title: 'Crop 4', imagePath: '/images/sample_enhancement_1.jpg', referenceImageUrl: '', selected: false, savedCropX: null, savedCropY: null, naturalWidth: 0, naturalHeight: 0, fitScale: 1, userScale: 1, minUserScale: 1, maxUserScale: 5, translateX: 0, translateY: 0 }
          ];
        }

        this.isLoading = false;
      },
      error: () => {
        this.errorMessage = 'Failed to load crop details.';
        this.isLoading = false;
      }
    });
  }

  toggleSelection(option: CropOption): void {
    option.selected = !option.selected;
    this.cropOptions = [...this.cropOptions];
  }

  onImageLoad(event: Event, option: CropOption): void {
    const img = event.target as HTMLImageElement;

    option.naturalWidth = img.naturalWidth;
    option.naturalHeight = img.naturalHeight;

    option.fitScale = Math.min(
      this.previewWidth / option.naturalWidth,
      this.previewHeight / option.naturalHeight
    );

    const coverScale = Math.max(
      this.fixedCropRect.width / (option.naturalWidth * option.fitScale),
      this.fixedCropRect.height / (option.naturalHeight * option.fitScale)
    );

    option.minUserScale = Math.max(0.35, +(coverScale * 0.55).toFixed(2));
    option.userScale = Math.max(option.userScale, option.minUserScale);

    const totalScale = this.getTotalScale(option);

    if (option.savedCropX !== null && option.savedCropY !== null) {
      // Reverse of buildCropSelection:
      // savedCropX = (fixedCropRect.x - translateX) / totalScale
      // => translateX = fixedCropRect.x - savedCropX * totalScale
      option.translateX = this.fixedCropRect.x - option.savedCropX * totalScale;
      option.translateY = this.fixedCropRect.y - option.savedCropY * totalScale;
    } else {
      const centered = this.getCenteredImagePosition(option);
      option.translateX = centered.x;
      option.translateY = centered.y;
    }

    const clamped = this.clampImagePosition(option, option.translateX, option.translateY);
    option.translateX = clamped.x;
    option.translateY = clamped.y;

    this.cropOptions = [...this.cropOptions];
  }

  startImageDrag(event: MouseEvent, option: CropOption): void {
    event.preventDefault();
    event.stopPropagation();

    this.dragState = {
      optionId: option.id,
      startClientX: event.clientX,
      startClientY: event.clientY,
      initialTranslateX: option.translateX,
      initialTranslateY: option.translateY
    };
  }

  onWheel(event: WheelEvent, option: CropOption): void {
    event.preventDefault();
    event.stopPropagation();

    if (!option.naturalWidth || !option.naturalHeight) return;

    const previewRect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const pointerX = event.clientX - previewRect.left;
    const pointerY = event.clientY - previewRect.top;

    const oldScale = this.getTotalScale(option);

    const imagePointX = (pointerX - option.translateX) / oldScale;
    const imagePointY = (pointerY - option.translateY) / oldScale;

    const delta = event.deltaY > 0 ? -0.12 : 0.12;
    const nextUserScale = this.clamp(
      +(option.userScale + delta).toFixed(2),
      option.minUserScale,
      option.maxUserScale
    );

    option.userScale = nextUserScale;

    const newScale = this.getTotalScale(option);

    option.translateX = pointerX - imagePointX * newScale;
    option.translateY = pointerY - imagePointY * newScale;

    const clamped = this.clampImagePosition(option, option.translateX, option.translateY);
    option.translateX = clamped.x;
    option.translateY = clamped.y;

    this.cropOptions = [...this.cropOptions];
  }

  onZoomSliderChange(event: Event, option: CropOption): void {
    const input = event.target as HTMLInputElement;
    const targetScale = parseFloat(input.value);
    if (isNaN(targetScale)) return;

    this.applyZoom(option, targetScale);
  }

  stepZoom(option: CropOption, step: number): void {
    const targetScale = +(option.userScale + step).toFixed(2);
    this.applyZoom(option, targetScale);
  }

  private applyZoom(option: CropOption, targetUserScale: number): void {
    if (!option.naturalWidth || !option.naturalHeight) return;

    const nextUserScale = this.clamp(
      targetUserScale,
      option.minUserScale,
      option.maxUserScale
    );

    const oldScale = this.getTotalScale(option);

    const anchorX = this.fixedCropRect.x + this.fixedCropRect.width / 2;
    const anchorY = this.fixedCropRect.y + this.fixedCropRect.height / 2;

    const imagePointX = (anchorX - option.translateX) / oldScale;
    const imagePointY = (anchorY - option.translateY) / oldScale;

    option.userScale = nextUserScale;

    const newScale = this.getTotalScale(option);

    option.translateX = anchorX - imagePointX * newScale;
    option.translateY = anchorY - imagePointY * newScale;

    const clamped = this.clampImagePosition(option, option.translateX, option.translateY);
    option.translateX = clamped.x;
    option.translateY = clamped.y;

    this.cropOptions = [...this.cropOptions];
  }

  @HostListener('window:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.dragState) return;

    const option = this.cropOptions.find(item => item.id === this.dragState!.optionId);
    if (!option) return;

    const dx = event.clientX - this.dragState.startClientX;
    const dy = event.clientY - this.dragState.startClientY;

    const nextX = this.dragState.initialTranslateX + dx;
    const nextY = this.dragState.initialTranslateY + dy;

    const clamped = this.clampImagePosition(option, nextX, nextY);
    option.translateX = clamped.x;
    option.translateY = clamped.y;

    this.cropOptions = [...this.cropOptions];
  }

  @HostListener('window:mouseup')
  onMouseUp(): void {
    this.dragState = null;
  }

  onBackToJobs(): void {
    this.router.navigate(['/app/jobs']);
  }

  onNext(): void {
    if (this.selectedCount !== this.visibleCropCount) {
      this.showNoCropAlert = true;
      return;
    }

    if (this.isSavingCrops) return;
    this.isSavingCrops = true;
    this.errorMessage = '';

    const cropSelections: CropSelectionRequest[] = this.visibleCropOptions
      .filter(option => option.selected)
      .map(option => this.buildCropSelection(option));

    // Build the API payload — only referenceImageUrl is sent as the image identifier
    const payload = cropSelections.map(sel => {
      const srcOption = this.cropOptions.find(o => o.id === sel.cropType);
      return {
        cropType: sel.cropType,
        x: sel.x,
        y: sel.y,
        width: sel.width,
        height: sel.height,
        scale: sel.scale ?? null,
        topLeft: sel.topLeft ?? null,
        topRight: sel.topRight ?? null,
        bottomLeft: sel.bottomLeft ?? null,
        bottomRight: sel.bottomRight ?? null,
        referenceImageUrl: srcOption?.referenceImageUrl ?? null,
        referenceImageBlobPath: null
      };
    });

    const companyId = this.companyContextService.selectedCompanyId();
    this.jobsService.saveCropSelections(this.jobId, payload, companyId).subscribe({
      next: (res) => {
        this.isSavingCrops = false;
        if (res.success) {
          this.jobProcessingStateService.setCropSelections(this.jobId, cropSelections);
          this.router.navigate(['/app/jobs', this.jobId, 'photos']);
        } else {
          this.errorMessage = res.message || 'Failed to save crop selections.';
        }
      },
      error: () => {
        this.isSavingCrops = false;
        this.errorMessage = 'Failed to save crop selections.';
      }
    });
  }

  getImageStyle(option: CropOption): Record<string, string> {
    const totalScale = this.getTotalScale(option);

    return {
      width: `${option.naturalWidth * totalScale}px`,
      height: `${option.naturalHeight * totalScale}px`,
      left: `${option.translateX}px`,
      top: `${option.translateY}px`
    };
  }

  private buildCropSelection(option: CropOption): CropSelectionRequest {
    const totalScale = this.getTotalScale(option);

    const x = this.clamp(
      Math.round((this.fixedCropRect.x - option.translateX) / totalScale),
      0,
      option.naturalWidth
    );

    const y = this.clamp(
      Math.round((this.fixedCropRect.y - option.translateY) / totalScale),
      0,
      option.naturalHeight
    );

    const width = this.clamp(
      Math.round(this.fixedCropRect.width / totalScale),
      1,
      option.naturalWidth - x
    );

    const height = this.clamp(
      Math.round(this.fixedCropRect.height / totalScale),
      1,
      option.naturalHeight - y
    );

    return {
      cropType: option.id as CropType,
      x,
      y,
      width,
      height,
      scale: option.userScale,
      topLeft: { x, y },
      topRight: { x: x + width, y },
      bottomLeft: { x, y: y + height },
      bottomRight: { x: x + width, y: y + height }
    };
  }

  private getTotalScale(option: CropOption): number {
    return option.fitScale * option.userScale;
  }

  private getCenteredImagePosition(option: CropOption): { x: number; y: number } {
    const totalScale = this.getTotalScale(option);
    const width = option.naturalWidth * totalScale;
    const height = option.naturalHeight * totalScale;

    return {
      x: (this.previewWidth - width) / 2,
      y: (this.previewHeight - height) / 2
    };
  }

  private clampImagePosition(option: CropOption, x: number, y: number): { x: number; y: number } {
    const totalScale = this.getTotalScale(option);
    const imageWidth = option.naturalWidth * totalScale;
    const imageHeight = option.naturalHeight * totalScale;

    const overscrollX = 40;
    const overscrollY = 50;

    const minX = this.fixedCropRect.x + this.fixedCropRect.width - imageWidth - overscrollX;
    const maxX = this.fixedCropRect.x + overscrollX;

    const minY = this.fixedCropRect.y + this.fixedCropRect.height - imageHeight - overscrollY;
    const maxY = this.fixedCropRect.y + overscrollY;

    return {
      x: this.clamp(x, minX, maxX),
      y: this.clamp(y, minY, maxY)
    };
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }
}